import cn from 'classnames';

import { Editor } from 'primereact/editor';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { CgClose } from 'react-icons/cg';

import { OButton, OInput } from '@src/shared';
import EmailInput from './EmailInput';

import { EUserType, TWebinarInviteesType } from '@src/types';

import styles from './inviteModal.module.scss';
import { IAttendee } from '@src/webinar/types';
import { useInviteModal } from '../hooks';

const InviteModalContent = ({
  userRole,
  email,
  title,
  onClose,
  setAttendeesIdList,
  attendee,
  isRadioFieldsVisible = true,
  showCloseButton = true,
}: {
  userRole?: TWebinarInviteesType;
  email?: string;
  title?: string;
  onClose: () => void;
  setAttendeesIdList?: (attendees: (prev: Set<string>) => Set<string>) => void;
  attendee?: IAttendee;
  isRadioFieldsVisible?: boolean;
  showCloseButton?: boolean;
}) => {
  const {
    role,
    emails,
    subject,
    body,
    isLoading,
    handleAddEmail,
    setRole,
    setEmails,
    setSubject,
    setBody,
    editorMaxHeight,
    isMobile,
  } = useInviteModal({
    userRole,
    email: email || '',
    onClose,
    attendee,
    setAttendeesIdList: setAttendeesIdList || (() => {}),
  });

  const renderHeader = () => (
    <div className={styles.customToolbar}>
      <span className="ql-formats">
        <button className="ql-bold" />
        <button className="ql-italic" />
        <button className="ql-underline" />
      </span>
      <span className={styles.toolbarSeparator} />
      <span className="ql-formats">
        <button className="ql-list" value="ordered" />
        <button className="ql-list" value="bullet" />
      </span>
      <span className={styles.toolbarSeparator} />
      <span className="ql-formats">
        <button className="ql-image" />
      </span>
      <span className="ql-formats group">
        <select className="ql-align" />
      </span>
    </div>
  );
  return (
    <>
      {showCloseButton && (
        <div className={cn('flex justify-between items-center mb-1', { 'mb-4': !!userRole })}>
          <div className="text-[20px] font-bold text-[#1e272c]">{title || 'Panel list invitation'}</div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-full flex items-center justify-center bg-[#f5f6f7] hover:bg-[#e5e6e7]"
          >
            <CgClose size={16} className="text-[#a42727]" />
          </button>
        </div>
      )}

      {isRadioFieldsVisible && (
        <div className="mb-4">
          {!userRole && (
            <label className="text-[#1e272c] text-[16px] font-medium mb-2">Invitation to Join Our Webinar as a</label>
          )}

          <RadioGroup row value={role} onChange={(e) => setRole(e.target.value as TWebinarInviteesType)}>
            <FormControlLabel
              value={EUserType.SPEAKER}
              control={
                <Radio
                  sx={{
                    color: '#AEBCCB', // unselected color
                    '&.Mui-checked': {
                      color: '#1a73e8', // selected color (blue)
                    },
                  }}
                />
              }
              label="Speaker"
              sx={{
                '.MuiFormControlLabel-label': {
                  color: '#1e272c',
                  fontSize: '16px',
                  fontWeight: 500,
                },
              }}
            />
            <FormControlLabel
              value={EUserType.CO_HOST}
              control={
                <Radio
                  sx={{
                    color: '#AEBCCB',
                    '&.Mui-checked': {
                      color: '#1a73e8',
                    },
                  }}
                />
              }
              label="Co-host"
              sx={{
                '.MuiFormControlLabel-label': {
                  color: '#1e272c',
                  fontSize: '16px',
                  fontWeight: 500,
                },
              }}
            />
          </RadioGroup>
        </div>
      )}
      {!userRole && (
        <div className="mb-4">
          <EmailInput value={emails} onChange={setEmails} />
        </div>
      )}

      <div className="mb-4">
        <label className="block text-[#004a66] text-xs font-semibold mb-1">Subject</label>
        <OInput
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
            }
          }}
          className="user-name-input"
        />
      </div>

      <div className="mb-4 flex-1 overflow-y-auto">
        <Editor
          value={body}
          headerTemplate={isMobile ? renderHeader() : null}
          modules={{
            table: false,
            'table-better': {
              language: 'en_US',
              menus: ['copy', 'delete'],
              toolbarButtons: {
                whiteList: ['bold', 'italic', 'underline', 'strike', 'list', 'align', 'link', 'image'],
              },
              toolbarTable: true,
            },
            keyboard: {
              bindings: {
                tab: false,
              },
            },
          }}
          onTextChange={(e) => setBody(e.htmlValue || '')}
          placeholder="Please enter your notes here..."
          style={{
            fontSize: '16px',
            minHeight: `${editorMaxHeight}px`,
            maxHeight: `${editorMaxHeight}px`,
            overflowY: 'auto',
            border: '1px solid #cfd8dc',
          }}
        />
      </div>
      <div className="sticky">
        <div className="flex justify-end">
          <OButton
            text="Send invite"
            onClick={() => handleAddEmail()}
            loading={isLoading}
            isDisabled={isLoading}
            outlined={!isMobile}
            size={!isMobile ? 'x-small' : 'full'}
          />
        </div>
      </div>
    </>
  );
};

export default InviteModalContent;
